import cn from "classnames";
import { useTranslation } from "react-i18next";
import type { DocumentTypeSchema } from "@noa/api-core";
import { schemaDialogProps } from "~/utils/schema-admin";
import { FieldDetail } from "./field-detail";
import { Button, Collapse, Dialog, Tag, Text, Title, Descriptions } from "~/components/ui";

type SchemaViewModalProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  schema: DocumentTypeSchema | null;
  onEdit?: () => void;
};

export function SchemaViewModal({ isOpen, setIsOpen, schema, onEdit }: SchemaViewModalProps) {
  const { t } = useTranslation("admin");

  if (!schema) return null;

  return (
    <Dialog
      title={t("schemaAdmin.modal.viewTitle")}
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      footer={
        onEdit ? (
          <Button variant="primary" onClick={onEdit}>
            {t("schemaAdmin.actions.edit")}
          </Button>
        ) : null
      }
      {...schemaDialogProps}
      className={cn(schemaDialogProps.className, "max-w-[1200px]")}
      width="90vw"
    >
      <Descriptions
        column={2}
        bordered
        size="small"
        className="mt-4"
        items={[
          {
            label: t("schemaAdmin.columns.schemaId"),
            children: (
              <>
                <Text code>{schema.schema_id}</Text>
                {schema.is_builtin && (
                  <Tag color="blue" className="ml-2">
                    {t("schemaAdmin.builtin")}
                  </Tag>
                )}
              </>
            ),
          },
          { label: t("schemaAdmin.columns.docType"), children: schema.doc_type },
          { label: t("schemaAdmin.columns.description"), children: schema.description },
          {
            label: t("schemaAdmin.columns.promptHint"),
            children: schema.prompt_hint || <Text tone="muted">—</Text>,
          },
          { label: t("schemaAdmin.columns.version"), children: schema.version },
          { label: t("schemaAdmin.columns.parser"), children: schema.parser || <Text tone="muted">default</Text> },
          {
            label: t("schemaAdmin.columns.extractImages"),
            children: schema.extract_page_images ? t("common:yes") : t("common:no"),
          },
          {
            label: t("schemaAdmin.columns.chunking"),
            children: (
              <>
                {schema.chunking?.strategy ? (
                  <>
                    {t(`schemaAdmin.chunking.${schema.chunking.strategy}`, { defaultValue: schema.chunking.strategy })}
                    {schema.chunking.min_chunk_chars && (
                      <Text tone="muted"> (min: {schema.chunking.min_chunk_chars})</Text>
                    )}
                    {schema.chunking.strategy === "numbered_items" && schema.chunking.items_per_chunk && (
                      <Text tone="muted">
                        {" "}
                        ({t("schemaAdmin.chunking.itemsPerChunkPlaceholder")}: {schema.chunking.items_per_chunk})
                      </Text>
                    )}
                  </>
                ) : (
                  <Text tone="muted">{t("schemaAdmin.chunking.default")}</Text>
                )}
              </>
            ),
          },
          {
            label: t("schemaAdmin.columns.fields"),
            children: (
              <>
                {(schema.fields?.length ?? 0) > 0 ? (
                  <Text>
                    {schema.fields?.length} {t("schemaAdmin.fieldsCount")}
                  </Text>
                ) : (
                  <Text tone="muted">—</Text>
                )}
              </>
            ),
          },
        ]}
      />

      {(schema.fields?.length ?? 0) > 0 && (
        <>
          <Title level={5} className="mt-4">
            {t("schemaAdmin.fieldsList")}
          </Title>
          <Collapse
            size="small"
            className="max-h-[50vh] overflow-y-auto"
            items={(schema.fields ?? []).map((field, index) => ({
              key: String(index),
              label: (
                <span>
                  <Text strong className="font-mono">
                    {field.name}
                  </Text>
                  {field.type && <Tag className="ml-2 text-xs">{field.type}</Tag>}
                  {field.required && (
                    <Tag color="red" className="text-xs">
                      {t("schemaAdmin.field.required")}
                    </Tag>
                  )}
                </span>
              ),
              children: <FieldDetail field={field} t={t} />,
            }))}
          />
        </>
      )}
    </Dialog>
  );
}
